import { AuthenticationException } from "@/types/client-exceptions"
import { SimpleHttpError, SimpleHttpResponse } from "@/types/http"
import { Optional } from "@/types/util"
import {
  AxiosInstance,
  AxiosRequestConfig,
  CanceledError,
  GenericAbortSignal,
} from "axios"

import { containsAuthPath } from "./auth-utils"
import { errorMessage, normalizeServerError } from "./exception-utils"
import { isClientSide } from "./is-client-side"
import { mtLocation } from "./mt-location"
import { SocketManager } from "./socket-manager"

export type BaseApiRequestConfig = {
  /**
   * Should be used only if handling auth exception locally.
   * When true, prevents automatic redirection to auth page on authentication failures.
   */
  skipAuthHandling?: boolean
} & AxiosRequestConfig

export class BaseApi {
  private readonly skipAuthHandling: boolean

  constructor(
    private axios: AxiosInstance,
    options?: { skipAuthHandling?: boolean }
  ) {
    this.skipAuthHandling = options?.skipAuthHandling ?? false
  }

  protected async head(path: string, customConfig?: BaseApiRequestConfig) {
    return this.executeRequest(
      () => this.axios.head(path, this.config(customConfig)),
      path,
      customConfig
    )
  }

  protected async get<TResponse>(
    path: string,
    customConfig?: BaseApiRequestConfig
  ): Promise<TResponse> {
    return this.executeRequest(
      async () => {
        const response = await this.axios.get<
          TResponse | StreamingResponseData
        >(path, this.config(customConfig))
        return this.handleResponseData(
          "get",
          path,
          response.data,
          customConfig?.signal
        )
      },
      path,
      customConfig
    )
  }

  protected async post<TResponse, TRequest = unknown>(
    path: string,
    requestData?: TRequest,
    customConfig?: BaseApiRequestConfig
  ): Promise<TResponse> {
    const response = await this.postWithStatus<TResponse, TRequest>(
      path,
      requestData,
      customConfig
    )
    return response.data
  }

  protected async postWithStatus<TResponse, TRequest = unknown>(
    path: string,
    requestData?: TRequest,
    customConfig?: BaseApiRequestConfig
  ): Promise<{ data: TResponse; status: number }> {
    return this.executeRequest(
      async () => {
        const response = await this.axios.post<
          TResponse | StreamingResponseData
        >(path, requestData, this.config(customConfig))
        const data = await this.handleResponseData<TResponse>(
          "post",
          path,
          response.data,
          customConfig?.signal
        )
        return { data, status: response.status }
      },
      path,
      customConfig
    )
  }

  protected async put<TResponse, TRequest = unknown>(
    path: string,
    requestData?: TRequest,
    customConfig?: BaseApiRequestConfig
  ): Promise<TResponse> {
    return this.executeRequest(
      async () => {
        const response = await this.axios.put<
          TResponse | StreamingResponseData
        >(path, requestData, this.config(customConfig))
        return this.handleResponseData(
          "put",
          path,
          response.data,
          customConfig?.signal
        )
      },
      path,
      customConfig
    )
  }

  protected async patch<TResponse, TRequest = unknown>(
    path: string,
    requestData: TRequest,
    customConfig?: BaseApiRequestConfig
  ): Promise<TResponse> {
    return this.executeRequest(
      async () => {
        const response = await this.axios.patch<
          TResponse | StreamingResponseData
        >(path, requestData, this.config(customConfig))
        return this.handleResponseData(
          "patch",
          path,
          response.data,
          customConfig?.signal
        )
      },
      path,
      customConfig
    )
  }

  protected async delete<TResponse>(
    path: string,
    customConfig?: BaseApiRequestConfig
  ): Promise<TResponse> {
    return this.executeRequest(
      async () => {
        const response = await this.axios.delete<
          TResponse | StreamingResponseData
        >(path, this.config(customConfig))

        return this.handleResponseData(
          "delete",
          path,
          response.data,
          customConfig?.signal
        )
      },
      path,
      customConfig
    )
  }

  protected getAxios(): AxiosInstance {
    return this.axios
  }

  private async handleResponseData<TResponse>(
    httpMethod: string,
    path: string,
    data: TResponse | StreamingResponseData,
    signal?: Optional<GenericAbortSignal>
  ) {
    return new Promise<TResponse>((resolve, reject) => {
      if (signal?.addEventListener) {
        // this is to support axios's cancellation with our custom slow response requests
        signal.addEventListener("abort", () => {
          reject(new CanceledError("RequestAborted"))
        })
      }

      if (isStreamingResponseData(data)) {
        let socketManager: SocketManager
        try {
          socketManager = SocketManager.instance
        } catch (e) {
          throw new Error(
            `SocketManager instance not available for path ${path}: ${errorMessage(e)}`
          )
        }
        socketManager
          // The signal also withdraws the buffer wait, so the fetcher stops
          // polling for a response nobody will consume.
          .waitForResponse<TResponse>(
            data.streaming_response_id,
            signal ?? undefined
          )
          .then(resolve, (errorData) => {
            const message =
              (errorData &&
                typeof errorData === "object" &&
                errorData.message) ||
              "unknown error"

            const response: SimpleHttpResponse = {
              type: "SimpleHttpResponse",
              statusText: "streamed response",
              data: errorData,
            }

            // this will get handled in the execute request
            reject(
              new SimpleHttpError(message, {
                requestMethod: httpMethod,
                requestPath: path,
                response,
              })
            )
          })
      } else {
        resolve(data)
      }
    })
  }

  private config(
    customConfig?: BaseApiRequestConfig | null
  ): BaseApiRequestConfig {
    const baseConfig = customConfig ?? {}

    // If per-request skipAuthHandling is not explicitly set, use class-level default
    if (baseConfig.skipAuthHandling === undefined) {
      baseConfig.skipAuthHandling = this.skipAuthHandling
    }

    return baseConfig
  }

  private async executeRequest<T>(
    requestFn: () => Promise<T>,
    path: string,
    customConfig: BaseApiRequestConfig | undefined
  ): Promise<T> {
    const config = this.config(customConfig)

    // lazy import to avoid circular dependency
    const { refreshTokenIfNeeded, refreshAuthToken } =
      await import("@/lib/auth/client")

    try {
      if (isClientSide() && !config?.skipAuthHandling) {
        await refreshTokenIfNeeded()
        void SocketManager.check()
      }
      return await requestFn()
    } catch (error) {
      const normalizedError = normalizeServerError(error, { requestPath: path })

      // maybe some weird timing issue here where the refreshIfTokenNeeded didn't do its job
      // try one more time else let bubble up the error
      if (
        isClientSide() &&
        normalizedError instanceof AuthenticationException &&
        !config?.skipAuthHandling
      ) {
        // Pass current URL so failed refresh redirects back (unless it's an auth page)
        const currentUrl = mtLocation.href
        const errorCallbackUrl = containsAuthPath(currentUrl) ? "/" : currentUrl
        await refreshAuthToken(errorCallbackUrl)
        return await requestFn()
      }

      throw normalizedError
    }
  }
}

interface StreamingResponseData {
  streaming_response_id: string
}

function isStreamingResponseData(data: unknown): data is StreamingResponseData {
  return !!data && typeof data === "object" && "streaming_response_id" in data
}
